/**
 * PropertyLookupDrawer - A polished drawer for looking up property data.
 *
 * Features:
 * - Address autocomplete with Smarty suggestions
 * - Confirmation step before expensive API call
 * - Property data lookup via RTK Query
 * - Owner-focused data display with prominent styling
 * - Download/print report functionality
 * - Minimized, drawer, and fullscreen states
 */
import {
  AlertCircle,
  Building2,
  Calendar,
  Check,
  Copy,
  DollarSign,
  Download,
  Home,
  Landmark,
  Loader2,
  Mail,
  MapPin,
  Maximize2,
  Minimize2,
  Minus,
  RotateCcw,
  Ruler,
  Search,
  User,
  X,
} from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Z_INDEX_FINCH_ASSISTANT } from '@/components/ui/zIndex';
import { cn } from '@/lib/utils';
import {
  PropertyAttributes,
  PropertyDataResult,
  useSmartyPropertyLookupQuery,
  useSmartyPropertyReportPdfMutation,
} from '@/services/api/smartyService';

import { AddressAutocomplete } from '../ui/address-autocomplete/AddressAutocomplete';

type DrawerState = 'minimized' | 'drawer' | 'fullscreen';

interface PropertyLookupDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

interface SelectedAddress {
  street: string;
  address_line2: string | null;
  city: string;
  state: string;
  zipcode: string;
}

// Helper to format currency values
const formatCurrency = (value: string | null | undefined): string => {
  if (!value) return '-';
  const num = parseFloat(value);
  if (Number.isNaN(num)) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(num);
};

// Helper to format numbers with commas
const formatNumber = (value: string | null | undefined): string => {
  if (!value) return '-';
  const num = parseFloat(value);
  if (Number.isNaN(num)) return '-';
  return new Intl.NumberFormat('en-US').format(num);
};

// Helper to humanize snake_case
const humanize = (value: string | null | undefined): string => {
  if (!value) return '-';
  return value
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase())
    .trim();
};

// Generate printable HTML report
const generateReport = (result: PropertyDataResult): string => {
  const attrs = result.attributes;
  const address = result.matched_address;
  const fullAddress = address
    ? `${address.street}, ${address.city}, ${address.state} ${address.zipcode}`
    : 'Unknown Address';

  // Collect all non-empty attributes for the report
  const sections: { title: string; rows: [string, string][] }[] = [
    {
      title: 'Property Owner',
      rows: (() => {
        const ownerRows: [string, string][] = [];

        // Primary owner
        const primaryOwner = attrs.owner_full_name || attrs.owner_name;
        if (primaryOwner) {
          ownerRows.push(['Primary Owner', primaryOwner]);
        }

        // Additional owners (labeled clearly)
        if (attrs.owner_full_name_2) {
          ownerRows.push(['Additional Owner', attrs.owner_full_name_2]);
        }
        if (attrs.owner_full_name_3) {
          ownerRows.push(['Additional Owner', attrs.owner_full_name_3]);
        }

        // Owner type - deduplicate company info
        const isCompany = attrs.company_flag === 'owner_is_company';
        const ownershipType = humanize(attrs.ownership_type);
        const isOwnershipCompany = ownershipType.toLowerCase() === 'company';

        if (isCompany || isOwnershipCompany) {
          ownerRows.push(['Owner Type', 'Company']);
        } else if (attrs.ownership_type && ownershipType !== '-') {
          ownerRows.push(['Owner Type', ownershipType]);
        }

        // Occupancy
        const occupancy = humanize(attrs.owner_occupancy_status);
        if (occupancy !== '-') {
          ownerRows.push(['Occupancy Status', occupancy]);
        }

        // Mailing address - combine into one line if possible
        const mailingParts = [
          attrs.contact_full_address,
          attrs.contact_city,
          attrs.contact_state,
          attrs.contact_zip,
        ].filter(Boolean);
        if (mailingParts.length > 0) {
          ownerRows.push(['Mailing Address', mailingParts.join(', ')]);
        }

        return ownerRows;
      })(),
    },
    {
      title: 'Property Details',
      rows: (
        [
          ['Year Built', attrs.year_built || '-'],
          ['Bedrooms', attrs.bedrooms || '-'],
          ['Bathrooms', attrs.bathrooms_total || attrs.bathrooms || '-'],
          ['Stories', attrs.stories_number || '-'],
          ['Units', attrs.unit_count || '-'],
          [
            'Living Area (sq ft)',
            formatNumber(attrs.living_sqft || attrs.building_sqft),
          ],
          [
            'Building Area (sq ft)',
            formatNumber(attrs.gross_sqft || attrs.building_sqft),
          ],
          [
            'Property Type',
            humanize(attrs.land_use_standard || attrs.property_use_description),
          ],
          ['Land Use', humanize(attrs.land_use_group)],
        ] as [string, string][]
      ).filter(([, v]) => v && v !== '-'),
    },
    {
      title: 'Assessment & Taxes',
      rows: (
        [
          ['Total Market Value', formatCurrency(attrs.total_market_value)],
          ['Assessed Value', formatCurrency(attrs.assessed_value)],
          ['Land Value', formatCurrency(attrs.market_land_value)],
          ['Improvement Value', formatCurrency(attrs.market_improvement_value)],
          ['Annual Tax Amount', formatCurrency(attrs.tax_billed_amount)],
          ['Tax Year', attrs.tax_fiscal_year || '-'],
          ['Tax Jurisdiction', attrs.tax_jurisdiction || '-'],
        ] as [string, string][]
      ).filter(([, v]) => v && v !== '-'),
    },
    {
      title: 'Sale History',
      rows: (
        [
          ['Last Sale Date', attrs.sale_date || '-'],
          [
            'Last Sale Price',
            formatCurrency(attrs.sale_amount || attrs.transfer_amount),
          ],
          ['Prior Sale Date', attrs.prior_sale_date || '-'],
          ['Prior Sale Price', formatCurrency(attrs.prior_sale_amount)],
        ] as [string, string][]
      ).filter(([, v]) => v && v !== '-'),
    },
    {
      title: 'Mortgage Information',
      rows: (
        [
          ['Loan Amount', formatCurrency(attrs.mortgage_amount)],
          ['Lender', attrs.lender_name || '-'],
          ['Mortgage Type', humanize(attrs.mortgage_type)],
          ['Due Date', attrs.mortgage_due_date || '-'],
        ] as [string, string][]
      ).filter(([, v]) => v && v !== '-'),
    },
    {
      title: 'Land & Location',
      rows: (
        [
          ['Lot Size (sq ft)', formatNumber(attrs.lot_sqft)],
          [
            'Acres',
            attrs.acres ? `${parseFloat(attrs.acres).toFixed(2)}` : '-',
          ],
          ['Parcel ID', attrs.parcel_account_number || attrs.parcel_id || '-'],
          ['County', attrs.situs_county || '-'],
          ['Zoning', attrs.zoning || '-'],
          ['Latitude', attrs.latitude || '-'],
          ['Longitude', attrs.longitude || '-'],
        ] as [string, string][]
      ).filter(([, v]) => v && v !== '-'),
    },
    {
      title: 'Building Features',
      rows: (
        [
          ['Heating', humanize(attrs.heat)],
          ['Air Conditioning', humanize(attrs.air_conditioning)],
          ['Fireplace', attrs.fireplace_number || humanize(attrs.fireplace)],
          ['Pool', humanize(attrs.pool)],
          ['Garage', humanize(attrs.garage)],
          ['Parking Spaces', attrs.parking_spaces || '-'],
          ['Construction Type', humanize(attrs.construction_type)],
          ['Roof Type', humanize(attrs.roof_type)],
          [
            'Exterior Walls',
            humanize(attrs.exterior_walls || attrs.exterior_wall),
          ],
          ['Foundation', humanize(attrs.foundation)],
          ['Basement (sq ft)', formatNumber(attrs.basement_sqft)],
        ] as [string, string][]
      ).filter(([, v]) => v && v !== '-'),
    },
    {
      title: 'Legal & Parcel Information',
      rows: (
        [
          ['Legal Description', attrs.legal_description || '-'],
          ['Subdivision', attrs.subdivision || '-'],
          ['Block', attrs.block1 || '-'],
          ['Lot', attrs.lot_1 || '-'],
          ['Tract', attrs.tract_number || '-'],
          ['FIPS Code', attrs.fips_code || '-'],
        ] as [string, string][]
      ).filter(([, v]) => v && v !== '-'),
    },
    {
      title: 'All Available Data',
      rows: Object.entries(attrs)
        .filter(([key, value]) => {
          // Skip extra_attributes object and empty values
          if (key === 'extra_attributes') return false;
          if (value === null || value === undefined || value === '')
            return false;
          return true;
        })
        .map(
          ([key, value]) =>
            [
              humanize(key),
              typeof value === 'string' ? value : String(value),
            ] as [string, string],
        )
        .sort((a, b) => a[0].localeCompare(b[0])),
    },
  ];

  const now = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Property Report - ${fullAddress}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: Helvetica, Arial, sans-serif;
      padding: 40px;
      max-width: 800px;
      margin: 0 auto;
      color: #1f2937;
      line-height: 1.5;
    }
    h1 {
      font-size: 24px;
      margin-bottom: 8px;
      color: #111827;
    }
    .subtitle {
      color: #6b7280;
      font-size: 14px;
      margin-bottom: 32px;
    }
    .address-banner {
      background: #f0fdf4;
      border: 1px solid #bbf7d0;
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 24px;
    }
    .address-banner p {
      font-weight: 600;
      color: #166534;
    }
    h2 {
      font-size: 16px;
      color: #374151;
      margin-top: 24px;
      margin-bottom: 12px;
      padding-bottom: 8px;
      border-bottom: 2px solid #e5e7eb;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 16px;
    }
    td {
      padding: 8px 0;
      border-bottom: 1px solid #f3f4f6;
      font-size: 14px;
    }
    td:first-child {
      color: #6b7280;
      width: 40%;
    }
    td:last-child {
      font-weight: 500;
      text-align: right;
    }
    .footer {
      margin-top: 40px;
      padding-top: 16px;
      border-top: 1px solid #e5e7eb;
      font-size: 12px;
      color: #9ca3af;
    }
    @media print {
      body { padding: 20px; }
      h2 { page-break-after: avoid; }
      table { page-break-inside: avoid; }
    }
  </style>
</head>
<body>
  <h1>Property Report</h1>
  <p class="subtitle">Generated on ${now}</p>
  <p class="subtitle" style="margin-top: -24px;">Data provided by Smarty Property Data API &bull; Key: ${result.smarty_key}</p>

  <div class="address-banner">
    <p>${fullAddress}</p>
  </div>

  ${sections
    .filter((s) => s.rows.length > 0)
    .map(
      (section) => `
    <h2>${section.title}</h2>
    <table>
      ${section.rows.map(([label, value]) => `<tr><td>${label}</td><td>${value}</td></tr>`).join('')}
    </table>
  `,
    )
    .join('')}

  <div class="footer">
    <p>Smarty Key: ${result.smarty_key}</p>
    <p>Data provided by Smarty Property Data API</p>
  </div>
</body>
</html>`;
};

// Copy button with feedback
const CopyButton: React.FC<{ text: string; className?: string }> = ({
  text,
  className,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className={cn(
        'p-1 rounded hover:bg-zinc-100 transition-colors',
        className,
      )}
      title="Copy to clipboard"
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-green-600" />
      ) : (
        <Copy className="h-3.5 w-3.5 text-zinc-400" />
      )}
    </button>
  );
};

// Stat card for key metrics
interface StatCardProps {
  label: string;
  value: string;
  icon: React.ReactNode;
  variant?: 'default' | 'highlight';
}

const StatCard: React.FC<StatCardProps> = ({
  label,
  value,
  icon,
  variant = 'default',
}) => (
  <div
    className={cn(
      'rounded-xl p-3 flex flex-col gap-1',
      variant === 'highlight'
        ? 'bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100'
        : 'bg-zinc-50 border border-zinc-100',
    )}
  >
    <div className="flex items-center gap-1.5 text-zinc-500">
      {icon}
      <span className="text-xs font-medium uppercase tracking-wide">
        {label}
      </span>
    </div>
    <p
      className={cn(
        'text-lg font-bold',
        variant === 'highlight' ? 'text-blue-700' : 'text-zinc-900',
      )}
    >
      {value}
    </p>
  </div>
);

// Data row component
interface DataRowProps {
  label: string;
  value: string | null | undefined;
  formatter?: (v: string | null | undefined) => string;
  copyable?: boolean;
}

const DataRow: React.FC<DataRowProps> = ({
  label,
  value,
  formatter,
  copyable,
}) => {
  const displayValue = formatter ? formatter(value) : value || '-';
  const shouldShow = displayValue !== '-';

  if (!shouldShow) return null;

  return (
    <div className="flex justify-between items-start py-2 border-b border-zinc-100 last:border-0">
      <span className="text-sm text-zinc-500">{label}</span>
      <div className="flex items-center gap-1">
        <span className="text-sm font-medium text-zinc-900 text-right">
          {displayValue}
        </span>
        {copyable && value && <CopyButton text={value} />}
      </div>
    </div>
  );
};

// Section header
interface SectionHeaderProps {
  title: string;
  icon: React.ReactNode;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ title, icon }) => (
  <div className="flex items-center gap-2 mb-3">
    <div className="p-1.5 rounded-lg bg-zinc-100">{icon}</div>
    <h3 className="font-semibold text-zinc-800">{title}</h3>
  </div>
);

// Address confirmation card - shown before making API call
const AddressConfirmCard: React.FC<{
  address: SelectedAddress;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}> = ({ address, onConfirm, onCancel, isLoading }) => {
  const fullAddress = [
    address.street,
    address.city,
    address.state,
    address.zipcode,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="rounded-2xl border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 p-5">
      <div className="flex items-start gap-3 mb-4">
        <div className="p-2.5 rounded-xl bg-blue-100">
          <MapPin className="h-5 w-5 text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-zinc-800 mb-1">Confirm Address</h4>
          <p className="text-sm text-zinc-600">
            Please verify the address before proceeding.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl p-4 mb-4 border border-blue-100">
        <p className="font-medium text-zinc-900 text-lg">{address.street}</p>
        {address.address_line2 && (
          <p className="text-zinc-700">{address.address_line2}</p>
        )}
        <p className="text-zinc-600">
          {[address.city, address.state, address.zipcode]
            .filter(Boolean)
            .join(', ')}
        </p>
      </div>

      <div className="flex gap-2">
        <Button
          onClick={onConfirm}
          disabled={isLoading}
          className="flex-1 bg-blue-600 hover:bg-blue-700"
        >
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Looking up...
            </>
          ) : (
            <>
              <Search className="h-4 w-4 mr-2" />
              Look Up Property
            </>
          )}
        </Button>
        <Button variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
      </div>

      <p className="text-xs text-zinc-500 mt-3 text-center">
        Full address: {fullAddress}
      </p>
    </div>
  );
};

// Owner card component - the star of the show
const OwnerCard: React.FC<{ attrs: PropertyAttributes }> = ({ attrs }) => {
  const owners = [
    attrs.owner_full_name || attrs.owner_name,
    attrs.owner_full_name_2,
    attrs.owner_full_name_3,
  ].filter(Boolean);

  const mailingParts = [
    attrs.contact_full_address,
    attrs.contact_city,
    attrs.contact_state,
    attrs.contact_zip,
  ].filter(Boolean);
  const mailingAddress = mailingParts.join(', ');

  const isCompany = attrs.company_flag === 'owner_is_company';
  const occupancyStatus = attrs.owner_occupancy_status?.includes(
    'owner_occupied',
  )
    ? 'Owner Occupied'
    : attrs.owner_occupancy_status?.includes('not_owner')
      ? 'Not Owner Occupied'
      : humanize(attrs.owner_occupancy_status);

  return (
    <div className="rounded-2xl bg-gradient-to-br from-indigo-500 via-blue-500 to-blue-600 p-[1px] shadow-lg shadow-blue-500/20">
      <div className="rounded-2xl bg-gradient-to-br from-indigo-500 via-blue-500 to-blue-600 p-4">
        {/* Header */}
        <div className="flex items-center gap-2 mb-4">
          <div className="p-2 rounded-xl bg-white/20 backdrop-blur">
            <User className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="font-bold text-white text-lg">Property Owner</h3>
            <p className="text-blue-100 text-xs">Primary contact information</p>
          </div>
        </div>

        {owners.length > 0 ? (
          <div className="space-y-4">
            {/* Primary Owner */}
            <div className="bg-white/10 backdrop-blur rounded-xl p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-white text-lg break-words leading-tight">
                    {owners[0]}
                  </p>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {/* Show ownership type badge - but avoid redundancy with company flag */}
                    {(() => {
                      const ownershipLabel = humanize(attrs.ownership_type);
                      const isOwnershipCompany =
                        ownershipLabel.toLowerCase() === 'company';

                      // If it's a company, show "Company" badge (from either source, but only once)
                      if (isCompany || isOwnershipCompany) {
                        return (
                          <Badge
                            variant="secondary"
                            className="bg-white/20 text-white border-0 text-xs"
                          >
                            Company
                          </Badge>
                        );
                      }

                      // Otherwise show the ownership type if we have one
                      if (attrs.ownership_type && ownershipLabel !== '-') {
                        return (
                          <Badge
                            variant="secondary"
                            className="bg-white/20 text-white border-0 text-xs"
                          >
                            {ownershipLabel}
                          </Badge>
                        );
                      }

                      return null;
                    })()}
                    {occupancyStatus !== '-' && (
                      <Badge
                        variant="secondary"
                        className="bg-white/20 text-white border-0 text-xs"
                      >
                        {occupancyStatus}
                      </Badge>
                    )}
                  </div>
                </div>
                {owners[0] && (
                  <CopyButton
                    text={owners[0]}
                    className="text-white/60 hover:text-white hover:bg-white/10"
                  />
                )}
              </div>
            </div>

            {/* Additional Owners */}
            {owners.length > 1 && (
              <div className="space-y-2">
                <p className="text-xs text-blue-100 uppercase tracking-wide font-medium">
                  Additional Owners
                </p>
                {owners.slice(1).map((owner, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between bg-white/10 backdrop-blur rounded-lg px-3 py-2"
                  >
                    <span className="text-white text-sm font-medium break-words">
                      {owner}
                    </span>
                    {owner && (
                      <CopyButton
                        text={owner}
                        className="text-white/60 hover:text-white hover:bg-white/10"
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Mailing Address */}
            {mailingAddress && (
              <div className="bg-white/10 backdrop-blur rounded-xl p-3">
                <div className="flex items-start gap-2">
                  <Mail className="h-4 w-4 text-blue-200 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-blue-200 uppercase tracking-wide font-medium mb-1">
                      Mailing Address
                    </p>
                    <p className="text-white text-sm break-words">
                      {mailingAddress}
                    </p>
                  </div>
                  <CopyButton
                    text={mailingAddress}
                    className="text-white/60 hover:text-white hover:bg-white/10"
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white/10 backdrop-blur rounded-xl p-4 text-center">
            <p className="text-blue-100">No owner information available</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Property result display component
const PropertyResult: React.FC<{
  result: PropertyDataResult;
  onDownload: () => void;
  isPdfLoading?: boolean;
}> = ({ result, onDownload, isPdfLoading }) => {
  const attrs = result.attributes;

  // Key metrics for the stat cards
  const marketValue = formatCurrency(attrs.total_market_value);
  const yearBuilt = attrs.year_built || '-';
  const sqft = attrs.living_sqft || attrs.building_sqft;
  const formattedSqft = sqft ? `${formatNumber(sqft)} sf` : '-';

  return (
    <div className="space-y-6">
      {/* Matched Address Banner with Download */}
      {result.matched_address && (
        <div className="flex items-center gap-2 text-sm bg-emerald-50 text-emerald-700 rounded-xl px-4 py-3 border border-emerald-200">
          <div className="p-1 rounded-lg bg-emerald-100">
            <Check className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <p className="font-medium">
              {result.matched_address.street}, {result.matched_address.city},{' '}
              {result.matched_address.state} {result.matched_address.zipcode}
            </p>
          </div>
          <Button
            size="sm"
            variant="ghost"
            onClick={onDownload}
            disabled={isPdfLoading}
            className="text-emerald-700 hover:text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
          >
            {isPdfLoading ? (
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            ) : (
              <Download className="h-4 w-4 mr-1" />
            )}
            {isPdfLoading ? 'Generating...' : 'PDF Report'}
          </Button>
        </div>
      )}

      {/* Owner Card - The Hero Section */}
      <OwnerCard attrs={attrs} />

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-2">
        <StatCard
          label="Market Value"
          value={marketValue}
          icon={<DollarSign className="h-3.5 w-3.5" />}
          variant="highlight"
        />
        <StatCard
          label="Year Built"
          value={yearBuilt}
          icon={<Calendar className="h-3.5 w-3.5" />}
        />
        <StatCard
          label="Living Area"
          value={formattedSqft}
          icon={<Ruler className="h-3.5 w-3.5" />}
        />
      </div>

      {/* Property Details */}
      <div className="rounded-xl border border-zinc-200 bg-white p-4">
        <SectionHeader
          title="Property Details"
          icon={<Home className="h-4 w-4 text-zinc-600" />}
        />
        <DataRow label="Bedrooms" value={attrs.bedrooms} />
        <DataRow
          label="Bathrooms"
          value={attrs.bathrooms_total || attrs.bathrooms}
        />
        <DataRow label="Stories" value={attrs.stories_number} />
        <DataRow label="Units" value={attrs.unit_count} />
        <DataRow
          label="Property Type"
          value={attrs.land_use_standard || attrs.property_use_description}
          formatter={humanize}
        />
        <DataRow
          label="Land Use"
          value={attrs.land_use_group}
          formatter={humanize}
        />
      </div>

      {/* Assessment & Taxes */}
      <div className="rounded-xl border border-zinc-200 bg-white p-4">
        <SectionHeader
          title="Assessment & Taxes"
          icon={<Landmark className="h-4 w-4 text-zinc-600" />}
        />
        <DataRow
          label="Market Value"
          value={attrs.total_market_value}
          formatter={formatCurrency}
        />
        <DataRow
          label="Assessed Value"
          value={attrs.assessed_value}
          formatter={formatCurrency}
        />
        <DataRow
          label="Land Value"
          value={attrs.market_land_value}
          formatter={formatCurrency}
        />
        <DataRow
          label="Improvement Value"
          value={attrs.market_improvement_value}
          formatter={formatCurrency}
        />
        <DataRow
          label="Annual Tax"
          value={attrs.tax_billed_amount}
          formatter={formatCurrency}
        />
        <DataRow label="Tax Year" value={attrs.tax_fiscal_year} />
        <DataRow label="Jurisdiction" value={attrs.tax_jurisdiction} />
      </div>

      {/* Sale History */}
      {(attrs.sale_date || attrs.sale_amount) && (
        <div className="rounded-xl border border-zinc-200 bg-white p-4">
          <SectionHeader
            title="Sale History"
            icon={<DollarSign className="h-4 w-4 text-zinc-600" />}
          />
          <DataRow label="Last Sale Date" value={attrs.sale_date} />
          <DataRow
            label="Last Sale Price"
            value={attrs.sale_amount || attrs.transfer_amount}
            formatter={formatCurrency}
          />
          {attrs.prior_sale_date && (
            <>
              <div className="h-2" />
              <DataRow label="Prior Sale Date" value={attrs.prior_sale_date} />
              <DataRow
                label="Prior Sale Price"
                value={attrs.prior_sale_amount}
                formatter={formatCurrency}
              />
            </>
          )}
        </div>
      )}

      {/* Mortgage */}
      {(attrs.mortgage_amount || attrs.lender_name) && (
        <div className="rounded-xl border border-zinc-200 bg-white p-4">
          <SectionHeader
            title="Mortgage"
            icon={<Building2 className="h-4 w-4 text-zinc-600" />}
          />
          <DataRow
            label="Loan Amount"
            value={attrs.mortgage_amount}
            formatter={formatCurrency}
          />
          <DataRow label="Lender" value={attrs.lender_name} />
          <DataRow
            label="Type"
            value={attrs.mortgage_type}
            formatter={humanize}
          />
          <DataRow label="Due Date" value={attrs.mortgage_due_date} />
        </div>
      )}

      {/* Land & Location */}
      <div className="rounded-xl border border-zinc-200 bg-white p-4">
        <SectionHeader
          title="Land & Location"
          icon={<MapPin className="h-4 w-4 text-zinc-600" />}
        />
        <DataRow
          label="Lot Size"
          value={attrs.lot_sqft}
          formatter={(v) => (v ? `${formatNumber(v)} sq ft` : '-')}
        />
        <DataRow
          label="Acres"
          value={attrs.acres}
          formatter={(v) => (v ? `${parseFloat(v).toFixed(2)} acres` : '-')}
        />
        <DataRow
          label="Parcel ID"
          value={attrs.parcel_account_number || attrs.parcel_id}
          copyable
        />
        <DataRow label="County" value={attrs.situs_county} />
        <DataRow label="Zoning" value={attrs.zoning} />
        {attrs.latitude && attrs.longitude && (
          <DataRow
            label="Coordinates"
            value={`${parseFloat(attrs.latitude).toFixed(6)}, ${parseFloat(attrs.longitude).toFixed(6)}`}
            copyable
          />
        )}
      </div>

      {/* Building Features */}
      {(attrs.heat ||
        attrs.air_conditioning ||
        attrs.pool ||
        attrs.garage ||
        attrs.fireplace) && (
        <div className="rounded-xl border border-zinc-200 bg-white p-4">
          <SectionHeader
            title="Building Features"
            icon={<Building2 className="h-4 w-4 text-zinc-600" />}
          />
          <DataRow label="Heating" value={attrs.heat} formatter={humanize} />
          <DataRow
            label="Air Conditioning"
            value={attrs.air_conditioning}
            formatter={humanize}
          />
          <DataRow
            label="Fireplace"
            value={attrs.fireplace_number || attrs.fireplace}
          />
          <DataRow label="Pool" value={attrs.pool} formatter={humanize} />
          <DataRow label="Garage" value={attrs.garage} formatter={humanize} />
          <DataRow label="Parking Spaces" value={attrs.parking_spaces} />
          <DataRow
            label="Construction"
            value={attrs.construction_type}
            formatter={humanize}
          />
          <DataRow
            label="Roof Type"
            value={attrs.roof_type}
            formatter={humanize}
          />
        </div>
      )}

      {/* Smarty Key */}
      <div className="flex items-center justify-between text-xs text-zinc-400 pt-2">
        <span>Smarty Key: {result.smarty_key}</span>
        <CopyButton text={result.smarty_key} />
      </div>
    </div>
  );
};

// Empty state component
const EmptyState: React.FC<{ size?: 'sm' | 'lg' }> = ({ size = 'sm' }) => (
  <div
    className={cn(
      'flex flex-col items-center justify-center text-center',
      size === 'lg' ? 'py-16' : 'py-8',
    )}
  >
    <div
      className={cn(
        'rounded-2xl bg-gradient-to-br from-zinc-100 to-zinc-50 flex items-center justify-center mb-4',
        size === 'lg' ? 'w-20 h-20' : 'w-14 h-14',
      )}
    >
      <Building2
        className={cn('text-zinc-300', size === 'lg' ? 'h-10 w-10' : 'h-7 w-7')}
      />
    </div>
    <h4 className="font-semibold text-zinc-700 mb-1">Property Lookup</h4>
    <p className="text-sm text-zinc-500 max-w-[200px]">
      Enter an address to look up property ownership and details
    </p>
  </div>
);

export const PropertyLookupDrawer: React.FC<PropertyLookupDrawerProps> = ({
  isOpen,
  onClose,
}) => {
  const [state, setState] = useState<DrawerState>('drawer');
  const [isAnimating, setIsAnimating] = useState(false);
  const [addressValue, setAddressValue] = useState('');
  const [clearKey, setClearKey] = useState(0); // Used to reset autocomplete
  // Pending address = selected from autocomplete but not yet confirmed
  const [pendingAddress, setPendingAddress] = useState<SelectedAddress | null>(
    null,
  );
  // Confirmed address = user clicked "Look Up Property" - triggers API call
  const [confirmedAddress, setConfirmedAddress] =
    useState<SelectedAddress | null>(null);

  // RTK Query hook - only triggers when confirmedAddress is set
  const {
    data: propertyData,
    isLoading,
    isFetching,
    error,
  } = useSmartyPropertyLookupQuery(
    confirmedAddress
      ? {
          street: confirmedAddress.address_line2
            ? `${confirmedAddress.street} ${confirmedAddress.address_line2}`
            : confirmedAddress.street,
          city: confirmedAddress.city,
          state: confirmedAddress.state,
          zipcode: confirmedAddress.zipcode,
        }
      : { freeform: '' },
    {
      skip: !confirmedAddress,
    },
  );

  // PDF generation mutation
  const [generatePdf, { isLoading: isPdfLoading }] =
    useSmartyPropertyReportPdfMutation();

  const transitionToState = (newState: DrawerState) => {
    setIsAnimating(true);
    setTimeout(() => {
      setState(newState);
      setIsAnimating(false);
    }, 150);
  };

  // When user selects from autocomplete - just set pending, don't trigger API
  const handleAddressSelect = useCallback(
    (address: {
      address_line1: string;
      address_line2: string | null;
      city: string;
      state: string;
      postal_code: string;
      country: string;
    }) => {
      const selected = {
        street: address.address_line1,
        address_line2: address.address_line2,
        city: address.city,
        state: address.state,
        zipcode: address.postal_code,
      };
      setPendingAddress(selected);
      // Clear any previous confirmed address/results
      setConfirmedAddress(null);
    },
    [],
  );

  // User confirms - now trigger the API call
  const handleConfirmLookup = useCallback(() => {
    if (pendingAddress) {
      setConfirmedAddress(pendingAddress);
    }
  }, [pendingAddress]);

  // Cancel pending selection
  const handleCancelPending = useCallback(() => {
    setPendingAddress(null);
    setAddressValue('');
  }, []);

  const handleClear = useCallback(() => {
    setAddressValue('');
    setPendingAddress(null);
    setConfirmedAddress(null);
    setClearKey((k) => k + 1); // Force autocomplete to reset
  }, []);

  const handleDownload = useCallback(async () => {
    const result = propertyData?.results?.[0];
    if (!result) return;

    const html = generateReport(result);
    const address = result.matched_address;
    const filename = address
      ? `property-report-${address.street?.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`
      : 'property-report';

    try {
      const blob = await generatePdf({ body: { html, filename } }).unwrap();

      // Create download link from blob
      const url = URL.createObjectURL(blob as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to generate PDF:', err);
      // Fallback to HTML download if PDF generation fails
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  }, [propertyData, generatePdf]);

  if (!isOpen) return null;

  // Only use result when we have a confirmed address (RTK Query caches old data)
  const result = confirmedAddress ? propertyData?.results?.[0] : undefined;
  const showLoading = isLoading || isFetching;
  const errorMessage = error
    ? 'error' in error
      ? String(error.error)
      : 'Failed to fetch property data'
    : null;

  // Determine what to show in content area
  const renderMainContent = (size: 'sm' | 'lg' = 'sm') => {
    // Loading state
    if (showLoading) {
      return (
        <div
          className={cn(
            'flex flex-col items-center justify-center gap-3',
            size === 'lg' ? 'h-48' : 'h-40',
          )}
        >
          <Loader2
            className={cn(
              'animate-spin text-blue-600',
              size === 'lg' ? 'h-10 w-10' : 'h-8 w-8',
            )}
          />
          <p className="text-sm text-zinc-500">Looking up property...</p>
        </div>
      );
    }

    // Error state
    if (errorMessage) {
      return (
        <Alert variant="destructive" className="rounded-xl">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      );
    }

    // No results after confirmed lookup
    if (confirmedAddress && !result) {
      return (
        <Alert className="rounded-xl bg-amber-50 border-amber-200 text-amber-800">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            No property data found for this address. Try a different address or
            check the spelling.
          </AlertDescription>
        </Alert>
      );
    }

    // Show result
    if (result) {
      return (
        <PropertyResult
          result={result}
          onDownload={handleDownload}
          isPdfLoading={isPdfLoading}
        />
      );
    }

    // Pending address - show confirmation card
    if (pendingAddress) {
      return (
        <AddressConfirmCard
          address={pendingAddress}
          onConfirm={handleConfirmLookup}
          onCancel={handleCancelPending}
          isLoading={showLoading}
        />
      );
    }

    // Empty state
    return <EmptyState size={size} />;
  };

  const renderContent = () => {
    // Minimized state
    if (state === 'minimized') {
      return (
        <div
          className={cn(
            'fixed bottom-4 right-4 bg-white rounded-2xl shadow-xl border border-zinc-200 transition-all duration-300',
            isAnimating ? 'opacity-0 scale-95' : 'opacity-100 scale-100',
          )}
          style={{ zIndex: Z_INDEX_FINCH_ASSISTANT }}
        >
          <div className="flex items-center p-3 gap-3 cursor-pointer hover:bg-zinc-50 rounded-2xl">
            <div className="p-2 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500">
              <Building2 className="h-4 w-4 text-white" />
            </div>
            <span className="font-semibold text-sm text-zinc-700">
              Property Lookup
            </span>
            <div className="flex items-center gap-1 ml-2">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  transitionToState('drawer');
                }}
                className="p-1.5 hover:bg-zinc-100 rounded-lg transition-colors"
                title="Open"
              >
                <Maximize2 className="h-4 w-4 text-zinc-500" />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onClose();
                }}
                className="p-1.5 hover:bg-zinc-100 rounded-lg transition-colors"
                title="Close"
              >
                <X className="h-4 w-4 text-zinc-500" />
              </button>
            </div>
          </div>
        </div>
      );
    }

    // Drawer state
    if (state === 'drawer') {
      return (
        <div
          className="fixed bottom-4 right-4 bg-zinc-50 rounded-2xl shadow-2xl border border-zinc-200 transition-all duration-300 flex flex-col w-[440px] h-[640px] overflow-hidden"
          style={{ zIndex: Z_INDEX_FINCH_ASSISTANT }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-zinc-200 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500">
                <Building2 className="h-4 w-4 text-white" />
              </div>
              <span className="font-bold text-zinc-800">Property Lookup</span>
            </div>
            <div className="flex items-center gap-0.5">
              <button
                type="button"
                onClick={() => transitionToState('minimized')}
                className="p-2 hover:bg-zinc-100 rounded-lg transition-colors"
                title="Minimize"
              >
                <Minus className="h-4 w-4 text-zinc-500" />
              </button>
              <button
                type="button"
                onClick={() => transitionToState('fullscreen')}
                className="p-2 hover:bg-zinc-100 rounded-lg transition-colors"
                title="Fullscreen"
              >
                <Maximize2 className="h-4 w-4 text-zinc-500" />
              </button>
              <button
                type="button"
                onClick={onClose}
                className="p-2 hover:bg-zinc-100 rounded-lg transition-colors"
                title="Close"
              >
                <X className="h-4 w-4 text-zinc-500" />
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="px-4 py-3 bg-white border-b border-zinc-200 flex-shrink-0">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <AddressAutocomplete
                  key={clearKey}
                  value={addressValue}
                  onChange={(value) => {
                    setAddressValue(value);
                    // If typing something different, clear pending
                    if (pendingAddress && value !== pendingAddress.street) {
                      setPendingAddress(null);
                    }
                  }}
                  onAddressSelect={handleAddressSelect}
                  placeholder="Search address..."
                  disabled={showLoading}
                  inputClassName="pl-9"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" />
              </div>
              {(result ||
                pendingAddress ||
                confirmedAddress ||
                addressValue) && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleClear}
                  disabled={showLoading}
                  title="Clear and start over"
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 min-h-0">
            {renderMainContent('sm')}
          </div>
        </div>
      );
    }

    // Fullscreen state
    return (
      <div
        className={cn(
          'fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300',
          isAnimating ? 'opacity-0' : 'opacity-100',
        )}
        style={{ zIndex: Z_INDEX_FINCH_ASSISTANT }}
        role="presentation"
        onClick={() => transitionToState('drawer')}
      >
        {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events */}
        <div
          className={cn(
            'fixed inset-4 bg-zinc-50 rounded-2xl shadow-2xl transition-all duration-300 flex flex-col overflow-hidden',
            isAnimating ? 'scale-95 opacity-0' : 'scale-100 opacity-100',
          )}
          style={{ zIndex: Z_INDEX_FINCH_ASSISTANT + 1 }}
          role="dialog"
          aria-modal="true"
          aria-label="Property Lookup Fullscreen"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 bg-white border-b border-zinc-200 flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500">
                <Building2 className="h-5 w-5 text-white" />
              </div>
              <span className="font-bold text-lg text-zinc-800">
                Property Lookup
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => transitionToState('minimized')}
                className="p-2 hover:bg-zinc-100 rounded-lg transition-colors"
                title="Minimize"
              >
                <Minus className="h-4 w-4 text-zinc-500" />
              </button>
              <button
                type="button"
                onClick={() => transitionToState('drawer')}
                className="p-2 hover:bg-zinc-100 rounded-lg transition-colors"
                title="Exit fullscreen"
              >
                <Minimize2 className="h-4 w-4 text-zinc-500" />
              </button>
              <button
                type="button"
                onClick={onClose}
                className="p-2 hover:bg-zinc-100 rounded-lg transition-colors"
                title="Close"
              >
                <X className="h-4 w-4 text-zinc-500" />
              </button>
            </div>
          </div>

          {/* Search Bar */}
          <div className="px-6 py-4 bg-white border-b border-zinc-200 flex-shrink-0">
            <div className="flex gap-3 max-w-2xl mx-auto">
              <div className="flex-1 relative">
                <AddressAutocomplete
                  key={clearKey}
                  value={addressValue}
                  onChange={(value) => {
                    setAddressValue(value);
                    if (pendingAddress && value !== pendingAddress.street) {
                      setPendingAddress(null);
                    }
                  }}
                  onAddressSelect={handleAddressSelect}
                  placeholder="Enter an address to search..."
                  disabled={showLoading}
                  inputClassName="pl-10 h-11"
                />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400 pointer-events-none" />
              </div>
              {(result ||
                pendingAddress ||
                confirmedAddress ||
                addressValue) && (
                <Button
                  variant="outline"
                  onClick={handleClear}
                  disabled={showLoading}
                  className="h-11"
                  title="Clear and start over"
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  New Lookup
                </Button>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 min-h-0">
            <div className="max-w-2xl mx-auto">{renderMainContent('lg')}</div>
          </div>
        </div>
      </div>
    );
  };

  return createPortal(renderContent(), document.body);
};

export default PropertyLookupDrawer;
